import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';

// The dialog reads its own description when it opens; the time left is
// announced again as the count crosses these marks. Every tick would drown a
// screen reader.
const ANNOUNCE_AT_SECONDS: readonly number[] = [30, 10];

const secondsLeft = (remainingMs: () => number): number =>
  Math.max(0, Math.ceil(remainingMs() / 1000));

export type SessionExpiryDialogProps = {
  open: boolean;
  remainingMs: () => number;
  onStay: () => void;
};

export const SessionExpiryDialog = ({
  open,
  remainingMs,
  onStay,
}: SessionExpiryDialogProps) => {
  const { t } = useTranslation();
  const announced = useRef<number[]>([]);
  const [seconds, setSeconds] = useState(() => secondsLeft(remainingMs));
  const [announceSeconds, setAnnounceSeconds] = useState<number | null>(null);

  // Fires as soon as the button's DOM node exists, moving focus into the
  // dialog every time it mounts (each open, given the default unmount-on-close).
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => {
    node?.focus();
  }, []);

  // Reseeds before paint, so a reopened dialog never shows a frame carrying
  // the previous cycle's countdown or announcement.
  useLayoutEffect(() => {
    announced.current = [];
    setAnnounceSeconds(null);

    if (open) {
      setSeconds(secondsLeft(remainingMs));
    }
  }, [open, remainingMs]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const interval = window.setInterval(() => {
      const left = secondsLeft(remainingMs);
      setSeconds(left);

      // Crossing, not equality: a late tick can skip the exact second, and a
      // single tick can pass both marks. Record every mark it crosses so the
      // count announces once, not again on the next tick.
      const crossed = ANNOUNCE_AT_SECONDS.filter(
        at => left <= at && !announced.current.includes(at)
      );

      if (crossed.length > 0) {
        announced.current.push(...crossed);
        setAnnounceSeconds(left);
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [open, remainingMs]);

  return (
    <Dialog
      open={open}
      onClose={onStay}
      aria-labelledby="session-expiry-dialog-title"
      aria-describedby="session-expiry-dialog-description"
    >
      <DialogTitle id="session-expiry-dialog-title">
        {t('SessionExpiryTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="session-expiry-dialog-description">
          {t('SessionExpiryBody', { count: seconds })}
        </DialogContentText>
        <Typography component="p" role="status" sx={visuallyHidden}>
          {announceSeconds === null
            ? ''
            : t('SessionExpiryBody', { count: announceSeconds })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onStay} ref={focusOnMount}>
          {t('SessionExpiryStay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
